<template>
    <form @submit.prevent="submitDetailForm">
        <div class="grid grid-cols-1 sm:grid-cols-3 gap-8">
            <form-input v-model="form.first_name"
                        label="First Name"
                        name="first_name"
                        id="first_name"
                        :required="true"
                        :error="getErrorFor('first_name')"
            ></form-input>

            <form-input v-model="form.last_name"
                        label="Last Name"
                        name="last_name"
                        id="last_name"
                        :required="true"
                        :error="getErrorFor('last_name')"
            ></form-input>

            <form-input v-model="form.phone"
                        label="Phone Number"
                        name="phone"
                        id="phone"
                        :required="true"
                        :error="getErrorFor('phone')"
            ></form-input>

            <div class="sm:col-span-3 text-sm">
                <p class="text-sm font-medium leading-5 text-gray-700">
                    We need to provide you with the correct sided feather on your paddle; are you
                </p>

                <div class="flex mt-3">
                    <form-radio v-model="form.hand"
                                value="Right"
                                class="mr-8"
                    >
                        Right Handed
                    </form-radio>

                    <form-radio v-model="form.hand"
                                value="Left"
                    >
                        Left Handed
                    </form-radio>
                </div>

                <p class="mt-2 text-xs text-red-600" v-if="hasError('hand')">
                    {{ getErrorFor('hand') }}
                </p>
            </div>

            <div class="sm:col-span-2 text-sm">
                <form-select v-model="form.height"
                             label="A lower centre of gravity makes for a more stable kayak; what is your height"
                             :options="heights"
                             track-by="value"
                             :error="getErrorFor('height')"
                ></form-select>
            </div>

            <div class="sm:col-span-2 text-sm">
                <form-input v-model="form.weight"
                            type="number"
                            label="Kayaks have a limited weight capacity; what is your body weight"
                            :required="true"
                            :error="getErrorFor('weight')"
                >
                    <template slot="addOn">kgs</template>
                </form-input>
            </div>

            <div class="sm:col-span-3 text-sm">
                <p class="text-sm font-medium leading-5 text-gray-700">
                    Basic flexibility allows for a more comfortable and safe experience overall; can you touch your toes
                    with feet at shoulder width?
                </p>

                <div class="flex mt-3">
                    <form-radio v-model="form.flexibility"
                                value="Yes"
                                class="mr-8"
                    >
                        Yes
                    </form-radio>

                    <form-radio v-model="form.flexibility"
                                value="No"
                    >
                        No
                    </form-radio>
                </div>

                <p class="mt-2 text-xs text-red-600" v-if="hasError('flexibility')">
                    {{ getErrorFor('flexibility') }}
                </p>
            </div>

            <div class="sm:col-span-3 text-sm">
                <p class="text-sm font-medium leading-5 text-gray-700">
                    Previous experience is useful to know for our Guides; have you paddled in deep, tidal water before?
                </p>

                <div class="flex mt-3">
                    <form-radio v-model="form.experience"
                                value="Yes"
                                class="mr-8"
                    >
                        Yes
                    </form-radio>

                    <form-radio v-model="form.experience"
                                value="No"
                    >
                        No
                    </form-radio>
                </div>

                <p class="mt-2 text-xs text-red-600" v-if="hasError('experience')">
                    {{ getErrorFor('experience') }}
                </p>
            </div>

            <div class="sm:col-span-3 text-sm">
                <p class="text-sm font-medium leading-5 text-gray-700">
                    Basic core strength and the ability to engage with the activity and environment makes for happier
                    paddlers and a better experience for everyone. What is your perceived fitness level?
                </p>

                <div class="mt-3 grid grid-cols-1 gap-3">
                    <form-radio v-model="form.core_strength"
                                value="Sedentary"
                                class="mr-8"
                                description="None at all"
                    >
                        Sedentary
                    </form-radio>

                    <form-radio v-model="form.core_strength"
                                value="Light activity"
                                description="Short walk the dog once a day"
                    >
                        Light activity
                    </form-radio>

                    <form-radio v-model="form.core_strength"
                                value="Medium activity"
                                description="45 minute workout 2-3 times a week with raised heart rate, raised breathing and sweat"
                    >
                        Medium activity
                    </form-radio>

                    <form-radio v-model="form.core_strength"
                                value="High activity"
                                description="Intense workouts as above or intense sports such as long distance cycling, running, triathlon, CrossFit et cetera"
                    >
                        High activity
                    </form-radio>

                    <p class="mt-2 text-xs text-red-600" v-if="hasError('core_strength')">
                        {{ getErrorFor('core_strength') }}
                    </p>
                </div>
            </div>

            <form-textarea v-model="form.dietary_requirements"
                           label="If you are a guest of our 'All Inclusive' fully catered, wild camping event, do you have any dietary requirements?"
                           name="dietary_requirements"
                           id="dietary_requirements"
                           class="sm:col-span-2"
                           :error="getErrorFor('dietary_requirements')"
            ></form-textarea>

            <div class="sm:col-span-3 text-sm">
                <p class="text-sm font-medium leading-5 text-gray-700">
                    You are obliged as part of your booking process to inform NOMAD Sea Kayaking of any medical
                    conditions, disabilities or phobias that may affect or limit your ability to participate. These
                    include but are not limited to;
                </p>

                <ul class="leading-5 text-gray-700 list-disc pl-10 mt-4">
                    <li>Diabetes.</li>
                    <li>Heart conditions.</li>
                    <li>Epilepsy.</li>
                    <li>Rheumatism/arthritis.</li>
                    <li>Back/knee problems &/or shoulder problems.</li>
                    <li>Hearing and/or site problems.</li>
                    <li>Seasickness.</li>
                    <li>Allergies.</li>
                    <li>Fears of deep water/swimming or any other relevant phobia/fears.</li>
                    <li>Joint injuries or previous shoulder dislocations.</li>
                    <li>Deepwater phobias.</li>
                    <li>The inability to swim.</li>
                    <li>Any 'other' disability or condition that may limit or affect your participation in some way.</li>
                </ul>

                <div class="flex items-center mt-4">
                    <form-toggle v-model="form.able"></form-toggle>
                    <div class="ml-2 text-gray-700">
                        I am fit and able to participate in this event
                    </div>
                </div>

                <div class="ml-12 mt-1 text-xs text-red-600" v-if="hasError('able')">
                    {{ getErrorFor('able') }}
                </div>
            </div>

            <form-textarea v-model="form.medical"
                           label="Please provide all relevant medical information that may affect your ability to participate here"
                           name="medical"
                           id="medical"
                           class="sm:col-span-2"
                           :error="getErrorFor('medical')"
            ></form-textarea>
        </div>

        <div class="font-semibold text-gray-600 mt-10 pt-6 text-sm border-t border-gray-200"
             v-if="! forBookingOwner"
        >
            <div class="flex items-center">
                <form-toggle v-model="form.marketing_permission"></form-toggle>
                <div class="ml-2">
                    Contact me with latest news and offers
                </div>
            </div>

            <div class="flex items-center mt-2">
                <form-toggle v-model="form.adult"></form-toggle>
                <div class="ml-2">
                    I confirm that I am at least 18 years of age
                </div>
            </div>

            <div class="ml-12 mt-1 text-xs text-red-600" v-if="hasError('adult')">
                {{ getErrorFor('adult') }}
            </div>

            <div class="flex items-center mt-2">
                <form-toggle v-model="form.terms"></form-toggle>
                <div class="ml-2">
                    I have read and agree to the <a href="/terms-and-conditions" target="_blank" class="text-primary-600 hover:underline">terms and conditions</a>
                </div>
            </div>

            <div class="ml-12 mt-1 text-xs text-red-600" v-if="hasError('terms')">
                {{ getErrorFor('terms') }}
            </div>
        </div>

        <div class="flex justify-end mt-6">
            <form-button type="submit">
                {{ forBookingOwner ? 'Confirm Details' : 'Accept Invitation' }}
            </form-button>
        </div>
    </form>
</template>

<script>
    import FormInput from "../../../../../components/form/Input";
    import FormRadio from "../../../../../components/form/Radio";
    import FormSelect from "../../../../../components/form/Select";
    import FormToggle from "../../../../../components/form/public/Toggle";
    import FormTextarea from "../../../../../components/form/Textarea";
    import FormButton from "../../../../../components/form/Button";

    import Form from "../../../../../mixins/Form";
    import Heights from "../../../../../mixins/Heights";

	export default {
	    data() {
	        return {
	            form: {
	                first_name: '',
	                last_name: '',
	                phone: '',
                    hand: '',
                    height: '',
                    weight: '',
                    flexibility: '',
                    experience: '',
                    core_strength: '',
                    dietary_requirements: '',
                    able: false,
                    medical: '',
                    marketing_permission: false,
                    terms: false,
                    adult: false,
                }
            }
        },

	    props: {
	        invitationId: {
	            type: Number,
            },

	        formData: {
	            type: Object,
                required: true,
            },

            forBookingOwner: {
	            type: Boolean,
                default: false,
            }
        },

        components: { FormInput, FormRadio, FormSelect, FormToggle, FormTextarea, FormButton },

        mixins: [Form, Heights],

        mounted() {
	        this.form.able = false;
        },

        methods: {
	        submitDetailForm() {
	            return this.forBookingOwner ? this.confirmDetails() : this.acceptInvitation();
            },

            confirmDetails() {
                this.patch(`/customer/${this.formData.id}/details`)
                    .then(() => {
                        this.$emit('details-updated');
                    });
            },

            acceptInvitation() {
                this.patch(`/customer/bookings/seats/invitation/${this.invitationId}`)
                    .then(() => {
                        window.location = '/customer/bookings';
                    })
            }
        }
	}
</script>
